/**
 * CoPaya portal entry (#1806).
 *
 * CoPaya is entered with a token the API signs for the current user. The token
 * lives for a minute and CoPaya consumes it single-use, so there is nothing to
 * cache here: every visit mints its own link.
 *
 * The status query is what decides whether the header link exists at all — the
 * signing secret is server-side, so the browser cannot tell a configured
 * deployment from an unconfigured one on its own.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useNotify } from '@/hooks/useNotify';
import { queryKeys } from './queryKeys';

export interface CopayaStatus {
  readonly enabled: boolean;
}

export interface CopayaEntryLink {
  readonly url: string;
}

export interface CopayaEntryLinkRequest {
  /** Opens the submit form instead of the project feed. */
  readonly prefillType?: 'bug' | 'feature' | 'question';
  readonly prefillSummary?: string;
}

export function useCopayaStatus() {
  return useQuery({
    queryKey: queryKeys.copaya.status(),
    queryFn: () => api.get<CopayaStatus>('/integrations/copaya/status'),
    // The answer changes only when the deployment does, and this runs on every
    // page as part of the header.
    staleTime: Infinity,
    retry: 1,
  });
}

export function useCopayaEntryLink() {
  return useMutation({
    mutationFn: (body: CopayaEntryLinkRequest = {}) =>
      api.post<CopayaEntryLink>('/integrations/copaya/entry-link', body),
    // A spent token cannot be presented twice: a retry would have to mint a new
    // one, and by then the user has already been told it failed.
    retry: false,
  });
}

export interface OpenCopayaOptions {
  /**
   * When the popup is blocked, the fallback navigates *this* page to CoPaya —
   * which would abort any still-running work (the screenshot flow's clipboard
   * write or download). Settle this promise first; rejection still navigates.
   */
  readonly sameTabNavigateAfter?: Promise<unknown>;
}

/**
 * Opens CoPaya in a new tab, optionally pre-filling the feedback form.
 *
 * `openCopaya` MUST be called synchronously inside a user gesture: the tab is
 * opened *before* the request that produces the URL. A `window.open` after an
 * await has lost the user gesture and popup blockers eat it, which is why the
 * blank tab is claimed first and navigated once the link arrives. `noopener`
 * cannot be passed for the same reason — it returns no handle — so the
 * back-reference is severed by hand instead.
 */
export function useOpenCopayaWindow() {
  const { t } = useTranslation();
  const notify = useNotify();
  const { mutate, isPending } = useCopayaEntryLink();

  const openCopaya = useCallback(
    (body: CopayaEntryLinkRequest = {}, options?: OpenCopayaOptions) => {
      const tab = window.open('about:blank', '_blank');
      if (tab) tab.opener = null;

      mutate(body, {
        onSuccess: ({ url }) => {
          if (tab) {
            tab.location.href = url;
          } else {
            // Popup blocked despite the gesture: same-tab is still the link
            // working, and the token expires either way.
            const gate = options?.sameTabNavigateAfter;
            if (gate) {
              void gate
                .catch(() => undefined)
                .then(() => window.location.assign(url));
            } else {
              window.location.assign(url);
            }
          }
        },
        onError: () => {
          tab?.close();
          notify.error(t('copayaOpenFailed'), t('copayaOpenFailedDesc'));
        },
      });
    },
    [mutate, notify, t],
  );

  return { openCopaya, isPending };
}
